import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Checkmark16Regular, Copy16Regular } from '@fluentui/react-icons';
import { BRAND_DOMAIN, brandName, type Mailbox } from '@gandhi/mail-protocol';
import { useEffect, useState, type ReactElement } from 'react';

const MAIL_APPS = [
  { id: 'outlook', name: 'Outlook' },
  { id: 'apple-mac', name: 'Apple Mail on a Mac' },
  { id: 'apple-ios', name: 'Apple Mail on iPhone or iPad' },
  { id: 'thunderbird', name: 'Thunderbird' },
  { id: 'gmail-android', name: 'Gmail on Android' },
] as const;

type MailAppId = (typeof MAIL_APPS)[number]['id'];

/** One value to type, and to copy. */
interface SetupSetting {
  readonly label: string;
  readonly value: string;
}

interface SetupStep {
  readonly title: string;
  readonly detail: string;
  readonly settings?: readonly SetupSetting[];
}

/**
 * The steps for one app and one mailbox. Every hostname is built from the mailbox's own
 * brand, so a Talt mailbox is never set up against Royal Aura's server names.
 *
 * Outlook and Thunderbird read their settings from `autodiscover.<domain>` and
 * `autoconfig.<domain>`, which Stalwart answers, so those two get an address and a
 * password and the server names only if that lookup fails. Apple Mail and Gmail read
 * neither, so they are typed in.
 */
function mailAppSteps(app: MailAppId, mailbox: Mailbox): readonly SetupStep[] {
  const address = mailbox.address;
  const domain = BRAND_DOMAIN[mailbox.brandId];
  const server = `mail.${domain}`;
  const identity = `id.${domain}`;
  const emailAddress: readonly SetupSetting[] = [{ label: 'Email address', value: address }];
  const servers: readonly SetupSetting[] = [
    { label: 'Incoming server (IMAP)', value: server },
    { label: 'IMAP port', value: '993' },
    { label: 'Outgoing server (SMTP)', value: server },
    { label: 'SMTP port', value: '465' },
    { label: 'User name', value: address },
  ];

  const first: SetupStep = {
    title: 'What you need',
    detail:
      `Your address, and the password you sign in to ${identity} with. It is your mail ` +
      'password too: there is no separate app password.',
    settings: emailAddress,
  };

  const rest: Record<MailAppId, readonly SetupStep[]> = {
    outlook: [
      {
        title: 'Add the account',
        detail: 'Choose File, then Add Account. Type your address and choose Connect.',
        settings: emailAddress,
      },
      {
        title: 'Let Outlook find the servers',
        detail:
          `It reads them from autodiscover.${domain}. Choose IMAP if it asks for an account ` +
          'type, type your password, and choose Connect.',
      },
      {
        title: 'If Outlook asks for the servers instead',
        detail:
          'Set both encryption methods to SSL/TLS and leave Secure Password Authentication ' +
          'unticked.',
        settings: servers,
      },
    ],
    thunderbird: [
      {
        title: 'Add the account',
        detail:
          'Choose Account Settings, then Account Actions, then Add Mail Account. Type your ' +
          'name, your address and your password, and choose Continue.',
        settings: emailAddress,
      },
      {
        title: 'Let Thunderbird find the servers',
        detail:
          `It reads them from autoconfig.${domain}. Check that it offers IMAP on ${server}, ` +
          'then choose Done.',
      },
      {
        title: 'If Thunderbird finds nothing',
        detail:
          'Choose Configure Manually and type these. Connection security is SSL/TLS and ' +
          'authentication is Normal password on both.',
        settings: servers,
      },
    ],
    'apple-mac': [
      {
        title: 'Add the account',
        detail:
          'In Mail, choose Mail, Settings, Accounts, then the plus button, then Other Mail Account.',
      },
      {
        title: 'Sign in',
        detail:
          'Type your name, your address and your password, then choose Sign In. Mail cannot ' +
          'look our settings up, so it asks for the servers next.',
        settings: emailAddress,
      },
      {
        title: 'Type the servers',
        detail: 'Set Account Type to IMAP, fill these in, then choose Sign In.',
        settings: servers,
      },
    ],
    'apple-ios': [
      {
        title: 'Add the account',
        detail: 'Open Settings, then Mail, Accounts, Add Account, Other, Add Mail Account.',
      },
      {
        title: 'Type your details',
        detail: 'Fill in your name, your address, your password and a description, then tap Next.',
        settings: emailAddress,
      },
      {
        title: 'Type the servers',
        detail:
          'Choose IMAP, then fill in Incoming and Outgoing Mail Server. Fill in the outgoing ' +
          'user name and password too — iOS calls them optional, and sending fails without them.',
        settings: servers,
      },
    ],
    'gmail-android': [
      {
        title: 'Add the account',
        detail: 'In Gmail, tap your picture, then Add another account, then Other.',
      },
      {
        title: 'Type your address',
        detail: 'Enter your address, tap Next, then choose Personal (IMAP).',
        settings: emailAddress,
      },
      {
        title: 'Type the servers',
        detail:
          'Enter your password, tap Next, then fill in the incoming and outgoing servers. ' +
          'Security type is SSL/TLS on both.',
        settings: servers,
      },
    ],
  };

  return [first, ...rest[app]];
}

const useStyles = makeStyles({
  address: { color: tokens.colorNeutralForeground2 },
  progress: { color: tokens.colorNeutralForeground3 },
  choices: { display: 'grid', gap: '8px', marginTop: '12px' },
  choice: { justifyContent: 'start' },
  settings: { display: 'grid', gap: '4px', marginTop: '12px' },
  setting: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, auto) auto auto',
    alignItems: 'center',
    columnGap: '8px',
    padding: '6px 8px',
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground3,
  },
  value: { fontFamily: tokens.fontFamilyMonospace, wordBreak: 'break-all' },
  copied: { color: tokens.colorNeutralForeground3 },
});

async function writeToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function CopyRow({ label, value }: SetupSetting): ReactElement {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div className={styles.setting}>
      <Text size={200}>{label}</Text>
      <Text weight="semibold" className={styles.value}>
        {value}
      </Text>
      <Text size={200} role="status" className={styles.copied}>
        {copied ? 'Copied' : ''}
      </Text>
      <Button
        appearance="subtle"
        size="small"
        icon={copied ? <Checkmark16Regular /> : <Copy16Regular />}
        aria-label={`Copy ${label}`}
        onClick={() => {
          void writeToClipboard(value).then((ok) => {
            if (ok) setCopied(true);
          });
        }}
      />
    </div>
  );
}

export interface MailAppSetupProps {
  readonly mailbox: Mailbox;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * A walkthrough for putting this mailbox in a desktop or phone mail app: pick the app,
 * then move through its steps. It names the signed-in address and that brand's own
 * hostnames throughout, because a set of settings is worth nothing without knowing which
 * mailbox they open.
 */
export function MailAppSetup({ mailbox, open, onOpenChange }: MailAppSetupProps): ReactElement {
  return (
    <Dialog open={open} onOpenChange={(_event, data) => onOpenChange(data.open)}>
      <DialogSurface data-testid="mail-app-setup">
        <Walkthrough mailbox={mailbox} onClose={() => onOpenChange(false)} />
      </DialogSurface>
    </Dialog>
  );
}

/** Mounted with the surface, so reopening always starts on the app list. */
function Walkthrough({
  mailbox,
  onClose,
}: {
  readonly mailbox: Mailbox;
  readonly onClose: () => void;
}): ReactElement {
  const styles = useStyles();
  const [app, setApp] = useState<MailAppId | null>(null);
  const [index, setIndex] = useState(0);

  const steps = app === null ? [] : mailAppSteps(app, mailbox);
  const step = steps[index];

  return (
    <DialogBody>
      <DialogTitle>Set up {brandName(mailbox.brandId)} mail in another app</DialogTitle>
      <DialogContent>
        <Text block className={styles.address}>
          {mailbox.address}
        </Text>

        {step === undefined ? (
          <div className={styles.choices} role="group" aria-labelledby="mail-app-setup-choose">
            <Text as="h3" id="mail-app-setup-choose" size={400} weight="semibold">
              Which app?
            </Text>
            {MAIL_APPS.map((entry) => (
              <Button
                key={entry.id}
                className={styles.choice}
                onClick={() => {
                  setApp(entry.id);
                  setIndex(0);
                }}
              >
                {entry.name}
              </Button>
            ))}
          </div>
        ) : (
          <>
            <Text block size={200} className={styles.progress}>
              Step {index + 1} of {steps.length}
            </Text>
            <Text as="h3" block size={400} weight="semibold">
              {step.title}
            </Text>
            <Text block>{step.detail}</Text>
            {step.settings !== undefined && (
              <div className={styles.settings}>
                {step.settings.map((setting) => (
                  <CopyRow key={setting.label} {...setting} />
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === undefined ? (
          <Button appearance="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button
              appearance="secondary"
              onClick={() => {
                if (index === 0) setApp(null);
                else setIndex(index - 1);
              }}
            >
              Back
            </Button>
            {index + 1 < steps.length ? (
              <Button appearance="primary" onClick={() => setIndex(index + 1)}>
                Next
              </Button>
            ) : (
              <Button appearance="primary" onClick={onClose}>
                Done
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </DialogBody>
  );
}
